/**
 * CreateProjectOverlay - Mobile-first stepped wizard + Desktop scroll form
 *
 * MOBILE (< 640px):
 *   Full-screen bottom sheet with a 5-step wizard.
 *   Each step fits one viewport without scrolling.
 *   Progress bar at top. Back/Next navigation at bottom.
 *   Big 48px+ touch targets everywhere.
 *   Swipe-right-to-go-back support via touch events.
 *
 * DESKTOP (>= 640px):
 *   Polished version of the existing centered modal scroll form.
 *   All functionality preserved — minimized dock, parent search, etc.
 *
 * SHARED:
 *   Minimizable dock mode preserved for both.
 *   AttributionFlagModal preserved.
 *   All existing props/API preserved — drop-in replacement.
 *
 * LARGE FILE UPLOADS (direct-to-blob):
 *   Files larger than LARGE_FILE_THRESHOLD (10MB) bypass the Flask backend
 *   entirely. The project is created/updated first via the normal multipart
 *   request (carrying only the small files), then each large file is PUT
 *   directly to Azure Blob Storage using a pre-signed SAS URL from
 *   projectsAPI.uploadFileDirect(), and registered in the database via
 *   projectsAPI.finalizeUpload(). This sidesteps the Nginx/Gunicorn body
 *   size limits on Azure App Service. A progress bar is shown during
 *   direct uploads on both mobile and desktop.
 *
 * Place at: src/components/overlays/CreateProjectOverlay.jsx
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  X, Upload, Image, Plus, Trash2, GitFork, Target, Tag,
  MapPin, Package, Monitor, Layers, Search, ChevronDown, Edit3,
  Minimize2, Maximize2, ChevronUp, FileText, Flag, AlertTriangle,
  CheckCircle, ChevronLeft, ChevronRight, Camera, FolderOpen,
  Sparkles, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';
import { Button, Input, Textarea, Badge, Avatar } from '../ui';
import { ZONES, PROJECT_TYPES } from '../../config/zones';
import { api, projectsAPI } from '../../api';
import MentionTextarea from './MentionTextarea';

// ─────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────

const SHAPE_OPTIONS = [
  { id: 'round',        symbol: '●', label: 'Round' },
  { id: 'wide',         symbol: '▬', label: 'Wide' },
  { id: 'narrow',       symbol: '▲', label: 'Narrow' },
  { id: 'twisted',      symbol: '∿', label: 'Twisted' },
  { id: 'symmetrical',  symbol: '⬡', label: 'Symmetrical' },
  { id: 'asymmetrical', symbol: '⬠', label: 'Asymmetrical' },
  { id: 'rising',       symbol: '↑', label: 'Rising' },
  { id: 'sinking',      symbol: '↓', label: 'Sinking' },
  { id: 'expanding',    symbol: '◎', label: 'Expanding' },
  { id: 'contracting',  symbol: '⊙', label: 'Contracting' },
];

const ZONE_TAGS = {
  build: ['woodworking', '3d printing', 'architecture', 'fabrication', 'engineering'],
  beautiful: ['art', 'illustration', 'photography', 'graphic design', 'ui/ux'],
  sound: ['music', 'audio', 'podcast', 'composition', 'sound design'],
  taste: ['cooking', 'baking', 'recipe', 'culinary', 'food'],
  story: ['writing', 'film', 'animation', 'storytelling', 'documentary'],
  heal: ['health', 'medical', 'wellness', 'therapy', 'biology'],
  smart: ['code', 'programming', 'ai', 'data', 'robotics'],
  sell: ['business', 'marketing', 'startup', 'branding', 'entrepreneurship'],
};

// ── Reserved physical-item tags ──────────────────────────────────────────
// These render as colored badges in ProjectDetailOverlay and power
// marketplace filters. Students tap them like any other tag — no extra
// forms, no new DB columns. The tag IS the metadata.
const PHYSICAL_TAGS = {
  availability: {
    label: 'Availability',
    color: 'emerald',
    tags: ['for-sale', 'for-trade', 'to-borrow', 'free', 'pickup-only', 'will-ship'],
  },
  condition: {
    label: 'Condition',
    color: 'amber',
    tags: ['new', 'like-new', 'good', 'fair', 'worn'],
  },
  craft: {
    label: 'Craft',
    color: 'violet',
    tags: ['handmade', 'one-of-a-kind', 'prototype', 'sample', 'limited-run', 'final'],
  },
};

const ALL_PHYSICAL_TAGS = Object.values(PHYSICAL_TAGS).flatMap(g => g.tags);

const WIZARD_STEPS = [
  { id: 'basics',      label: 'Basics',      icon: Sparkles },
  { id: 'type',        label: 'Type & Feel', icon: Layers },
  { id: 'connections', label: 'Lineage',     icon: GitFork },
  { id: 'files',       label: 'Files',       icon: FolderOpen },
  { id: 'review',      label: 'Review',      icon: Check },
];

// ── Direct-to-blob upload threshold ──────────────────────────────────────
// Files larger than this skip the Flask backend entirely and PUT straight
// to Azure Blob Storage via a pre-signed SAS URL. This bypasses the Nginx
// reverse-proxy and Flask MAX_CONTENT_LENGTH limits on Azure App Service.
const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const parseTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.map(t => typeof t === 'string' ? t : t.name || '').filter(Boolean);
  if (typeof tags === 'string') {
    try { const p = JSON.parse(tags); return Array.isArray(p) ? p : []; }
    catch { return tags.split(',').map(t => t.trim()).filter(Boolean); }
  }
  return [];
};

// ─────────────────────────────────────────────────────────────────────────
// Mobile detection hook
// ─────────────────────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// ─────────────────────────────────────────────────────────────────────────
// Upload Progress Panel — shown during direct-to-blob large file uploads
// ─────────────────────────────────────────────────────────────────────────

const UploadProgressPanel = ({ progress }) => {
  if (!progress) return null;
  return (
    <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Upload className="w-4 h-4 text-purple-400 flex-shrink-0 animate-pulse" />
          <span className="text-xs text-zinc-300 truncate">{progress.fileName}</span>
        </div>
        <span className="text-xs font-medium text-purple-300 flex-shrink-0 ml-2">
          {progress.total > 1 ? `${progress.index}/${progress.total} · ` : ''}{progress.percent}%
        </span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-200"
          style={{ width: `${progress.percent}%` }}
        />
      </div>
      <p className="text-[10px] text-zinc-600 mt-1.5">
        Uploading directly to cloud storage — please keep this window open
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Attribution Flag Modal (preserved exactly)
// ─────────────────────────────────────────────────────────────────────────

const AttributionFlagModal = ({ project, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [suspectedSource, setSuspectedSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ projectId: project.id, reason, suspectedSource });
      setDone(true);
    } catch (err) {
      // Must NOT setDone(true) here — the success screen claims a human will
      // review the flag, and that would be a lie if the request failed.
      toast.error(
        err.response?.data?.error || 'Could not submit flag — please try again.'
      );
    }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {done ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
            <p className="text-white font-medium text-center">Flag submitted — our team will review it shortly.</p>
            <button onClick={onClose} className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl transition-colors text-sm">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-amber-500/15 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-lg">Flag Missing Attribution</h3>
                <p className="text-zinc-500 text-xs mt-0.5">Under CC BY-SA 4.0 all derivative works must credit their sources.</p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded-full">
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                  What attribution is missing? <span className="text-red-400">*</span>
                </label>
                <textarea value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. This project builds on @student's ceramic bowl design but doesn't link to it."
                  rows={3}
                  className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:border-amber-500/50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                  Link or name of the original work <span className="text-zinc-500">(optional)</span>
                </label>
                <input type="text" value={suspectedSource} onChange={e => setSuspectedSource(e.target.value)}
                  placeholder="Project name, URL, or @username"
                  className="w-full bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50" />
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed">
                The creator will be notified and can add the source project proactively.
                Linking a source enables automatic revenue sharing.
              </p>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose} className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl transition-colors text-sm">Cancel</button>
                <button type="button" onClick={handleSubmit} disabled={!reason.trim() || submitting}
                  className="flex-1 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-900 font-medium rounded-xl transition-colors text-sm">
                  {submitting ? 'Submitting...' : 'Submit Flag'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Progress Bar
// ─────────────────────────────────────────────────────────────────────────

const WizardProgressBar = ({ currentStep, totalSteps, steps }) => {
  const progress = ((currentStep + 1) / totalSteps) * 100;
  return (
    <div className="px-4 pt-3 pb-2">
      {/* Progress track */}
      <div className="relative h-1 bg-zinc-800 rounded-full overflow-hidden mb-3">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Step dots */}
      <div className="flex items-center justify-between">
        {steps.map((step, i) => {
          const StepIcon = step.icon;
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return (
            <div key={step.id} className="flex flex-col items-center gap-1">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300',
                isActive ? 'bg-purple-500 text-white scale-110' :
                isComplete ? 'bg-purple-500/20 text-purple-400' :
                'bg-zinc-800 text-zinc-600'
              )}>
                {isComplete ? <Check className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
              </div>
              <span className={cn(
                'text-[10px] font-medium transition-colors',
                isActive ? 'text-purple-400' : isComplete ? 'text-zinc-500' : 'text-zinc-700'
              )}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Step: Basics (Cover + Name + Description)
// ─────────────────────────────────────────────────────────────────────────

const StepBasics = ({
  name, setName, description, setDescription,
  coverPreview, onCoverChange, onCoverClear,
  coverInputRef, errors,
}) => (
  <div className="flex flex-col gap-5 px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
    {/* Cover image — big tap target */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">Cover Image</label>
      <div
        onClick={() => coverInputRef.current?.click()}
        className={cn(
          'relative h-44 rounded-2xl cursor-pointer transition-all active:scale-[0.98]',
          coverPreview
            ? 'border-0'
            : 'border-2 border-dashed border-zinc-700 active:border-purple-500/50'
        )}
      >
        {coverPreview ? (
          <>
            <img src={coverPreview} alt="Cover" className="w-full h-full object-cover rounded-2xl" />
            <div className="absolute inset-0 bg-black/20 rounded-2xl flex items-center justify-center opacity-0 active:opacity-100 transition-opacity">
              <Camera className="w-8 h-8 text-white" />
            </div>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onCoverClear(); }}
              className="absolute top-3 right-3 p-2 bg-zinc-900/80 rounded-full active:bg-zinc-800">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
            <div className="w-14 h-14 rounded-2xl bg-zinc-800/80 flex items-center justify-center mb-3">
              <Camera className="w-7 h-7 text-zinc-500" />
            </div>
            <p className="text-sm font-medium text-zinc-400">Tap to add a cover photo</p>
            <p className="text-xs text-zinc-600 mt-1">This becomes your node on the map</p>
          </div>
        )}
      </div>
      <input ref={coverInputRef} type="file" accept="image/*" onChange={onCoverChange} className="hidden" />
    </div>

    {/* Project name — large input */}
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-zinc-300">
        Project Name <span className="text-red-400">*</span>
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Give your project a name"
        className={cn(
          'w-full bg-zinc-800/50 border rounded-xl px-4 py-3.5 text-base text-white',
          'placeholder:text-zinc-600 focus:outline-none transition-colors',
          errors.name ? 'border-red-500/50 focus:border-red-500' : 'border-zinc-700/50 focus:border-purple-500/50'
        )}
        autoFocus
      />
      {errors.name && <p className="text-sm text-red-400">{errors.name}</p>}
    </div>

    {/* Description */}
    <div className="space-y-1.5">
      {/* Typing [[ links another project (backlinks) */}
      <MentionTextarea
        label="Description"
        value={description}
        onChange={setDescription}
        placeholder="What did you create? What inspired it?"
        rows={3}
        size="base"
      />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Step: Type & Feel (Project type + Shape picker)
// ─────────────────────────────────────────────────────────────────────────

const StepTypeAndFeel = ({
  projectType, setProjectType, location, setLocation,
  shapeTags, toggleShapeTag, tags, setTags, tagInput, setTagInput,
  addTag, removeTag, handleTagKeyDown, errors,
}) => (
  <div className="flex flex-col gap-5 px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
    {/* Project type — big cards */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-3">What kind of project?</label>
      <div className="flex flex-col gap-2.5">
        {Object.entries(PROJECT_TYPES).map(([key, type]) => {
          const icons = { physical: Package, digital: Monitor, hybrid: Layers };
          const Icon = icons[key];
          const isSelected = projectType === key;
          const descriptions = {
            physical: 'Something you can touch — wood, metal, clay, fabric',
            digital: 'Code, design files, recordings, documents',
            hybrid: 'A mix of physical making and digital work',
          };
          return (
            <button
              key={key}
              type="button"
              onClick={() => setProjectType(key)}
              className={cn(
                'flex items-center gap-4 p-4 rounded-2xl border transition-all active:scale-[0.98]',
                isSelected
                  ? 'border-purple-500/50 bg-purple-500/10 shadow-lg shadow-purple-500/5'
                  : 'border-zinc-700/50 bg-zinc-800/30 active:bg-zinc-800/60'
              )}
            >
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                isSelected ? 'bg-purple-500/20' : 'bg-zinc-800'
              )}>
                {Icon && <Icon className={cn('w-5 h-5', isSelected ? 'text-purple-400' : 'text-zinc-500')} />}
              </div>
              <div className="flex-1 text-left">
                <p className={cn('text-sm font-semibold', isSelected ? 'text-white' : 'text-zinc-300')}>
                  {type.label}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">{descriptions[key]}</p>
              </div>
              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>

    {/* Location — only for physical */}
    {projectType === 'physical' && (
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-zinc-300">
          Location <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-zinc-500" />
          <input
            type="text" value={location} onChange={(e) => setLocation(e.target.value)}
            placeholder="Where is this physical project?"
            className={cn(
              'w-full bg-zinc-800/50 border rounded-xl pl-11 pr-4 py-3.5 text-base text-white',
              'placeholder:text-zinc-600 focus:outline-none transition-colors',
              errors.location ? 'border-red-500/50' : 'border-zinc-700/50 focus:border-purple-500/50'
            )}
          />
        </div>
        {errors.location && <p className="text-sm text-red-400">{errors.location}</p>}
      </div>
    )}

    {/* Shape / Feel picker — 2 columns of 5 for big touch targets */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-zinc-300">
          How does it feel? <span className="text-red-400">*</span>
        </label>
        <span className={cn(
          'text-xs font-medium px-2 py-0.5 rounded-full',
          shapeTags.length === 0 ? 'text-zinc-600 bg-zinc-800/50' :
          shapeTags.length === 2 ? 'text-violet-400 bg-violet-500/15' : 'text-zinc-400 bg-zinc-800/50'
        )}>
          {shapeTags.length}/2
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SHAPE_OPTIONS.map(shape => {
          const isSelected = shapeTags.includes(shape.id);
          const isDisabled = !isSelected && shapeTags.length >= 2;
          return (
            <button
              key={shape.id}
              type="button"
              onClick={() => toggleShapeTag(shape.id)}
              disabled={isDisabled}
              className={cn(
                'flex items-center gap-3 py-3.5 px-4 rounded-xl border transition-all active:scale-[0.97]',
                isSelected
                  ? 'bg-violet-500/20 border-violet-500/60 text-violet-300 shadow-lg shadow-violet-500/10'
                  : isDisabled
                    ? 'bg-zinc-800/20 border-zinc-800 text-zinc-700 cursor-not-allowed'
                    : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 active:border-violet-500/40'
              )}
            >
              <span className="text-xl leading-none">{shape.symbol}</span>
              <span className="text-sm font-medium">{shape.label}</span>
            </button>
          );
        })}
      </div>
      {errors.shapeTags && <p className="text-xs text-red-400 mt-2">{errors.shapeTags}</p>}
      <p className="text-[11px] text-zinc-600 mt-2">Pick 1 or 2 shapes that capture the feeling of your work</p>
    </div>

    {/* Tags */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">Tags</label>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tags.map(tag => {
            const physGroup = Object.values(PHYSICAL_TAGS).find(g => g.tags.includes(tag));
            const pillColor = physGroup
              ? physGroup.color === 'emerald' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
              : physGroup.color === 'amber' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
              : 'bg-violet-500/15 text-violet-400 border-violet-500/30'
              : 'bg-zinc-800 text-zinc-300 border-zinc-700/50';
            return (
              <span key={tag} className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border', pillColor)}>
                {tag}
                <button type="button" onClick={() => removeTag(tag)} className="p-0.5 active:text-red-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <input
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        onKeyDown={handleTagKeyDown}
        enterKeyHint="done"
        placeholder="Type a tag..."
        className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-xl px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
      />
      <p className="text-[11px] text-zinc-600 mt-1.5">Press Enter after each tag — tags determine your project's zone on the map</p>
      <div className="mt-3 space-y-2.5">
        {Object.entries(ZONE_TAGS).map(([zoneId, zoneTags]) => {
          const available = zoneTags.filter(t => !tags.includes(t));
          if (available.length === 0) return null;
          const zone = ZONES[zoneId];
          if (!zone) return null;
          return (
            <div key={zoneId}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: zone.color }}>{zone.shortLabel}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {available.slice(0, 3).map(s => (
                  <button key={s} type="button" onClick={() => addTag(s)}
                    className="px-2.5 py-1.5 text-xs border rounded-full active:border-zinc-600 transition-colors"
                    style={{ color: `${zone.color}cc`, borderColor: `${zone.color}30` }}>
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Physical / Hybrid — show marketplace & condition tag suggestions */}
      {(projectType === 'physical' || projectType === 'hybrid') && (
        <div className="mt-4 space-y-3">
          {Object.entries(PHYSICAL_TAGS).map(([groupKey, group]) => {
            const unselected = group.tags.filter(t => !tags.includes(t));
            if (unselected.length === 0) return null;
            const colorMap = { emerald: 'text-emerald-400 border-emerald-500/30 active:bg-emerald-500/10', amber: 'text-amber-400 border-amber-500/30 active:bg-amber-500/10', violet: 'text-violet-400 border-violet-500/30 active:bg-violet-500/10' };
            return (
              <div key={groupKey}>
                <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5">{group.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {unselected.map(t => (
                    <button key={t} type="button" onClick={() => addTag(t)}
                      className={cn('px-2.5 py-1.5 text-xs border rounded-full transition-colors', colorMap[group.color] || 'text-zinc-500 border-zinc-800')}>
                      + {t}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Step: Connections (Parents + Challenge context)
// ─────────────────────────────────────────────────────────────────────────

const StepConnections = ({
  mode, challenge, selectedParents, removeParent,
  parentSearchQuery, setParentSearchQuery, parentSearchResults,
  showParentSearch, setShowParentSearch, addParent,
  onMinimize, editProject, setFlagTarget, justAddedParent,
}) => {
  const showSection = mode === 'edit' || mode === 'fork' || mode === 'multifork' || selectedParents.length > 0;

  return (
    <div className="flex flex-col gap-5 px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
      {/* Challenge context */}
      {challenge && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-300">Responding to Challenge</p>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{challenge.challenge_title || challenge.title}</p>
            </div>
          </div>
        </div>
      )}

      {/* Parent projects */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-zinc-300">
            {mode === 'edit' ? 'Inspired By / Forked From' : selectedParents.length === 1 ? 'Forking From' : 'Parent Projects'}
          </label>
          {mode !== 'edit' && (
            <button type="button" onClick={onMinimize}
              className="flex items-center gap-1.5 text-xs text-purple-400 active:text-purple-300 py-1 px-2 rounded-lg active:bg-purple-500/10">
              <Minimize2 className="w-3.5 h-3.5" />
              Browse map
            </button>
          )}
        </div>

        {/* Selected parents */}
        {selectedParents.length > 0 ? (
          <div className="space-y-2 mb-3">
            {selectedParents.map(parent => (
              <div key={parent.id} className={cn(
                'flex items-center gap-3 p-3 rounded-xl transition-all',
                justAddedParent === parent.id
                  ? 'bg-emerald-500/15 border border-emerald-500/30'
                  : 'bg-zinc-800/50 border border-zinc-700/30'
              )}>
                <div className="w-10 h-10 rounded-lg bg-zinc-700 overflow-hidden flex-shrink-0">
                  {(parent.image_url || parent.imageUrl)
                    ? <img src={parent.image_url || parent.imageUrl} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm font-bold">{parent.name?.[0]}</div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 truncate">{parent.name}</p>
                  {parent.author?.username && (
                    <p className="text-xs text-zinc-500">@{parent.author.username}</p>
                  )}
                </div>
                <button type="button" onClick={() => removeParent(parent.id)}
                  className="p-2 rounded-lg active:bg-zinc-700">
                  <X className="w-4 h-4 text-zinc-500" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 bg-zinc-800/30 border border-zinc-700/30 border-dashed rounded-2xl mb-3">
            <GitFork className="w-8 h-8 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-500 text-center">
              {mode === 'edit'
                ? 'No parent projects linked yet'
                : 'Search below or minimize to browse the map'}
            </p>
          </div>
        )}

        {/* Attribution flag prompt — edit mode */}
        {mode === 'edit' && editProject && (
          <div className="flex items-start gap-2.5 mb-3 p-3 bg-zinc-800/40 border border-zinc-700/50 rounded-xl">
            <Flag className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-500 leading-relaxed">
              Know of a project you should be crediting?
              Add it above, or{' '}
              <button type="button" onClick={() => setFlagTarget(editProject)}
                className="text-amber-400 active:text-amber-300 underline underline-offset-2">
                flag this project
              </button>
              {' '}for review.
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={parentSearchQuery}
            onChange={(e) => setParentSearchQuery(e.target.value)}
            onFocus={() => setShowParentSearch(true)}
            placeholder="Search for a project to link..."
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-xl pl-10 pr-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50 transition-colors"
          />
          {showParentSearch && parentSearchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl z-10 max-h-64 overflow-y-auto">
              {parentSearchResults.map(project => (
                <button key={project.id} type="button" onClick={() => addParent(project)}
                  className="w-full flex items-center gap-3 p-3 active:bg-zinc-700/50 transition-colors text-left border-b border-zinc-700/30 last:border-0">
                  <div className="w-10 h-10 rounded-lg bg-zinc-600 overflow-hidden flex-shrink-0">
                    {(project.image_url || project.imageUrl)
                      ? <img src={project.image_url || project.imageUrl} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-zinc-400 font-medium">{project.name?.[0]}</div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{project.name}</p>
                    <p className="text-xs text-zinc-500">by @{project.author?.username}</p>
                  </div>
                  <Plus className="w-4 h-4 text-purple-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Prompt for new projects without connections */}
      {!showSection && !challenge && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center mb-4">
            <GitFork className="w-8 h-8 text-zinc-700" />
          </div>
          <h3 className="text-sm font-semibold text-zinc-400 mb-1">No connections yet</h3>
          <p className="text-xs text-zinc-600 max-w-[240px] leading-relaxed">
            That's totally fine! You can always add connections later by editing your project.
          </p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Step: Files
// ─────────────────────────────────────────────────────────────────────────

const StepFiles = ({
  files, setFiles, existingFiles, removeExistingFile, fileInputRef,
}) => (
  <div className="flex flex-col gap-4 px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
    {/* Existing files */}
    {existingFiles.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2">Existing Files</label>
        <div className="space-y-2">
          {existingFiles.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/30">
              <FileText className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              <span className="text-sm text-zinc-300 flex-1 truncate">{f.name}</span>
              <button type="button" onClick={() => removeExistingFile(f.id)} className="p-2 active:bg-zinc-700 rounded-lg">
                <Trash2 className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* New files */}
    {files.length > 0 && (
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2">New Files</label>
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/30">
              <FileText className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-300 truncate">{f.name}</p>
                <p className="text-xs text-zinc-600">
                  {formatFileSize(f.size)}
                  {f.size > LARGE_FILE_THRESHOLD && (
                    <span className="text-purple-400/80 ml-1.5">· direct upload</span>
                  )}
                </p>
              </div>
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                className="p-2 active:bg-zinc-700 rounded-lg">
                <Trash2 className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Upload area — big button for mobile */}
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-zinc-700 rounded-2xl active:border-purple-500/50 active:bg-purple-500/5 transition-all"
    >
      <div className="w-14 h-14 rounded-2xl bg-zinc-800/80 flex items-center justify-center mb-3">
        <Upload className="w-7 h-7 text-zinc-500" />
      </div>
      <p className="text-sm font-medium text-zinc-400">Tap to upload files</p>
      <p className="text-xs text-zinc-600 mt-1">85+ file types · large files upload straight to cloud</p>
    </button>
    <input ref={fileInputRef} type="file" multiple onChange={(e) => {
      setFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
      e.target.value = '';
    }} className="hidden" />

    {files.length === 0 && existingFiles.length === 0 && (
      <p className="text-xs text-zinc-600 text-center">
        Files are optional — you can always add them later.
      </p>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Mobile Wizard Step: Review
// ─────────────────────────────────────────────────────────────────────────

const StepReview = ({
  name, description, projectType, tags, shapeTags, location,
  coverPreview, selectedParents, challenge, files, existingFiles,
  mode, errors, isSubmitting, getSubmitText,
}) => (
  <div className="flex flex-col gap-4 px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
    {/* Summary card */}
    <div className="bg-zinc-800/40 border border-zinc-700/40 rounded-2xl overflow-hidden">
      {coverPreview && (
        <img src={coverPreview} alt="Cover" className="w-full h-32 object-cover" />
      )}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-1">
          {name || <span className="text-zinc-600 italic">Untitled</span>}
        </h3>
        {description && (
          <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{description.replace(/\[\[p:\d{1,9}\]\]/g, '(linked project)')}</p>
        )}

        <div className="flex flex-wrap gap-2">
          {/* Type badge */}
          <span className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
            projectType === 'physical' ? 'bg-emerald-500/20 text-emerald-400' :
            projectType === 'hybrid' ? 'bg-amber-500/20 text-amber-400' :
            'bg-purple-500/20 text-purple-400'
          )}>
            {projectType === 'physical' ? <Package className="w-3 h-3" /> :
             projectType === 'hybrid' ? <Layers className="w-3 h-3" /> :
             <Monitor className="w-3 h-3" />}
            {PROJECT_TYPES[projectType]?.label}
          </span>

          {/* Shape tags */}
          {shapeTags.map(id => {
            const shape = SHAPE_OPTIONS.find(s => s.id === id);
            return shape ? (
              <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-500/15 text-violet-400 rounded-full text-xs font-medium">
                {shape.symbol} {shape.label}
              </span>
            ) : null;
          })}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.map(tag => {
              const physGroup = Object.values(PHYSICAL_TAGS).find(g => g.tags.includes(tag));
              const pillColor = physGroup
                ? physGroup.color === 'emerald' ? 'bg-emerald-500/15 text-emerald-400'
                : physGroup.color === 'amber' ? 'bg-amber-500/15 text-amber-400'
                : 'bg-violet-500/15 text-violet-400'
                : 'bg-zinc-700/50 text-zinc-400';
              return <span key={tag} className={cn('px-2 py-0.5 rounded-full text-xs', pillColor)}>{tag}</span>;
            })}
          </div>
        )}

        {/* Location */}
        {projectType === 'physical' && location && (
          <div className="flex items-center gap-1.5 mt-3 text-xs text-zinc-500">
            <MapPin className="w-3 h-3" />
            {location}
          </div>
        )}
      </div>
    </div>

    {/* Connections summary */}
    {(selectedParents.length > 0 || challenge) && (
      <div className="bg-zinc-800/40 border border-zinc-700/40 rounded-2xl p-4">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Connections</p>
        {challenge && (
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-zinc-300">Responding to: {challenge.challenge_title || challenge.title}</span>
          </div>
        )}
        {selectedParents.length > 0 && (
          <div className="flex items-center gap-2">
            <GitFork className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-zinc-300">
              Forked from {selectedParents.length} project{selectedParents.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    )}

    {/* Files summary */}
    {(files.length > 0 || existingFiles.length > 0) && (
      <div className="bg-zinc-800/40 border border-zinc-700/40 rounded-2xl p-4">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Files</p>
        <p className="text-sm text-zinc-300">
          {files.length + existingFiles.length} file{files.length + existingFiles.length !== 1 ? 's' : ''} attached
        </p>
        {files.some(f => f.size > LARGE_FILE_THRESHOLD) && (
          <p className="text-xs text-zinc-500 mt-1">
            {files.filter(f => f.size > LARGE_FILE_THRESHOLD).length} large file{files.filter(f => f.size > LARGE_FILE_THRESHOLD).length !== 1 ? 's' : ''} will upload directly to cloud storage after the project is created
          </p>
        )}
      </div>
    )}

    {/* License notice */}
    <p className="text-[11px] text-zinc-600 text-center leading-relaxed px-4">
      By creating this project you agree to share it under CC BY-SA 4.0.
      Anyone can fork it with attribution. Revenue sharing follows the lineage chain.
    </p>

    {/* Error display */}
    {errors.submit && (
      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
        <p className="text-sm text-red-400">{errors.submit}</p>
      </div>
    )}
  </div>
);


// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════

export const CreateProjectOverlay = ({
  mode = 'new',
  parentProjects = [],
  challenge = null,
  editProject = null,
  onClose,
  onSuccess,
  onSearchProjects,
  onMinimize,
  onMaximize,
  externalParentToAdd,
  onExternalParentConsumed,
}) => {
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const isMobile = useIsMobile();

  // ── Form state (shared between mobile wizard and desktop form) ────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState('digital');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [location, setLocation] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [selectedParents, setSelectedParents] = useState(parentProjects);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [justAddedParent, setJustAddedParent] = useState(null);
  const [shapeTags, setShapeTags] = useState([]);
  const [flagTarget, setFlagTarget] = useState(null);

  // ── Direct-to-blob upload progress ────────────────────────────────────
  // { fileName, percent, index, total } while a large file is uploading,
  // null otherwise.
  const [uploadProgress, setUploadProgress] = useState(null);

  // ── Mobile wizard state ───────────────────────────────────────────────
  const [wizardStep, setWizardStep] = useState(0);

  // Compute which wizard steps to show based on mode
  const activeSteps = useMemo(() => {
    // Always show all steps — connections step adapts its content based on mode
    return WIZARD_STEPS;
  }, []);

  // ── Populate form for edit mode ───────────────────────────────────────
  // If the passed-in editProject has files (from ProjectDetailOverlay's
  // freshProject), we use them directly.  If not (map data is lightweight),
  // we fetch the full project once.  Either way, this runs exactly once
  // per editProject.id — no re-fetch when the user removes files locally.
  useEffect(() => {
    if (mode !== 'edit' || !editProject) return;

    setName(editProject.name || '');
    setDescription(editProject.description || '');
    setProjectType(editProject.project_type || editProject.projectType || 'digital');
    setTags(parseTags(editProject.tags));
    setLocation(editProject.location || '');
    setCoverPreview(editProject.image_url || editProject.imageUrl || null);
    setFilesToRemove([]);

    const rawShapes = editProject.shape_tags || editProject.shapeTags || '';
    const parsed = Array.isArray(rawShapes)
      ? rawShapes.map(s => String(s).trim()).filter(Boolean)
      : String(rawShapes).split(',').map(s => s.trim()).filter(Boolean);
    setShapeTags(parsed);

    const enriched = editProject.parent_projects || editProject.parentProjects || [];
    if (enriched.length > 0) {
      setSelectedParents(enriched);
    } else {
      const parentIds = editProject.parent_project_ids || editProject.parentProjectIds || [];
      if (parentIds.length > 0) {
        setSelectedParents(parentIds.map(id => ({ id, name: `Project #${id}`, image_url: null, author: null })));
        if (onSearchProjects) {
          Promise.all(parentIds.map(id => onSearchProjects(`id:${id}`)))
            .then(results => {
              const hydrated = results.flat().filter(Boolean).filter(p => parentIds.includes(p.id));
              if (hydrated.length > 0) setSelectedParents(hydrated);
            })
            .catch(() => {});
        }
      } else {
        setSelectedParents([]);
      }
    }

    // ── Populate files: use passed-in data or self-fetch ──────────────
    const passedFiles = editProject.files || [];
    if (passedFiles.length > 0) {
      setExistingFiles(passedFiles);
    } else {
      // No files in the data we received — fetch the full project
      let cancelled = false;
      (async () => {
        try {
          const res = await projectsAPI.getById(editProject.id);
          const proj = res.data?.project || res.data;
          if (cancelled) return;
          const fetched = [
            ...(proj.digital_items || []).map(item => ({
              id: item.id, name: item.name || item.file_name,
              file_url: item.file_url, file_type: item.file_type, type: 'digital',
            })),
            ...(proj.physical_items || []).map(item => ({
              id: item.id, name: item.name, image_url: item.image_url, type: 'physical',
            })),
          ];
          setExistingFiles(fetched); // even if empty — that's the truth
        } catch (err) {
          console.warn('Could not fetch project files:', err.message);
          setExistingFiles([]);
        }
      })();
      return () => { cancelled = true; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editProject?.id]);

  // ── External parent additions from minimized dock ─────────────────────
  useEffect(() => {
    if (externalParentToAdd && isMinimized) {
      const alreadyAdded = selectedParents.some(p => p.id === externalParentToAdd.id);
      if (!alreadyAdded) {
        setSelectedParents(prev => [...prev, externalParentToAdd]);
        setJustAddedParent(externalParentToAdd.id);
        setTimeout(() => setJustAddedParent(null), 2000);
      }
      onExternalParentConsumed?.();
    }
  }, [externalParentToAdd, isMinimized, selectedParents, onExternalParentConsumed]);

  // ── Click outside / Escape (desktop only) ─────────────────────────────
  useEffect(() => {
    if (isMinimized || isMobile) return;
    const handleClickOutside = (e) => {
      // Don't close if the AttributionFlagModal is open — it renders
      // outside overlayRef as a fixed z-[60] overlay, so clicks inside
      // it would incorrectly trigger close.
      if (flagTarget) return;
      // Don't close while the form is submitting — the PUT request
      // would be cancelled when the component unmounts.
      if (isSubmitting) return;
      if (overlayRef.current && !overlayRef.current.contains(e.target)) onClose?.();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (flagTarget) return; // Let the flag modal handle its own Escape
        if (isSubmitting) return; // Don't abandon an in-flight upload
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEscape); };
  }, [onClose, isMinimized, isMobile, flagTarget, isSubmitting]);

  // ── Escape on mobile = go back or close ───────────────────────────────
  useEffect(() => {
    if (!isMobile || isMinimized) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isSubmitting) return; // Don't abandon an in-flight upload
        if (wizardStep > 0) setWizardStep(prev => prev - 1);
        else onClose?.();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobile, isMinimized, wizardStep, onClose, isSubmitting]);

  // ── Parent search ─────────────────────────────────────────────────────
  useEffect(() => {
    if (parentSearchQuery.length < 2) { setParentSearchResults([]); return; }
    const search = async () => {
      try {
        const results = await onSearchProjects?.(parentSearchQuery);
        setParentSearchResults((results || []).filter(p => !selectedParents.find(sp => sp.id === p.id)).slice(0, 5));
      } catch {}
    };
    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [parentSearchQuery, selectedParents, onSearchProjects]);

  // ── Shared handlers ───────────────────────────────────────────────────
  const handleCoverChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setCoverImage(f); setCoverPreview(URL.createObjectURL(f)); }
  };
  const handleCoverClear = () => { setCoverImage(null); setCoverPreview(null); };
  const handleFilesChange = (e) => { setFiles(prev => [...prev, ...Array.from(e.target.files || [])]); };
  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files || [])]); };
  const addTag = (tag) => { const t = tag.trim().toLowerCase(); if (t && !tags.includes(t)) setTags(prev => [...prev, t]); setTagInput(''); };
  const removeTag = (tag) => setTags(prev => prev.filter(t => t !== tag));
  const handleTagKeyDown = (e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) removeTag(tags[tags.length - 1]); };
  const toggleShapeTag = (id) => {
    setShapeTags(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= 2) return prev;
      return [...prev, id];
    });
  };
  const addParent = (project) => { setSelectedParents(prev => [...prev, project]); setParentSearchQuery(''); setShowParentSearch(false); };
  const removeParent = (id) => setSelectedParents(prev => prev.filter(p => p.id !== id));
  const removeExistingFile = (id) => { setFilesToRemove(prev => [...prev, id]); setExistingFiles(prev => prev.filter(f => f.id !== id)); };

    const handleFlagSubmit = async ({ projectId, reason, suspectedSource }) => {
    // No optional-chaining, no empty catch. The previous version called
    // projectsAPI.flagAttribution?.() — a method that does not exist on the
    // client — so it evaluated to undefined, nothing was ever sent, and the
    // modal still showed "our team will review it shortly".
    // Errors must propagate so AttributionFlagModal can show a failure state.
    await api.post('/moderation/reports', {
      target_type: 'project',
      target_id: projectId,
      reason_category: 'missing_attribution',
      description: suspectedSource
        ? `${reason}\n\nSuspected source: ${suspectedSource}`
        : reason,
    });
  };

  // ── Validation ────────────────────────────────────────────────────────
  const validateStep = (step) => {
    const errs = {};
    if (step === 0 || step === 'all') {
      if (!name.trim()) errs.name = 'Project name is required';
      if (name.length > 100) errs.name = 'Name must be under 100 characters';
    }
    if (step === 1 || step === 'all') {
      if (projectType === 'physical' && !location.trim()) errs.location = 'Location required for physical projects';
      if (shapeTags.length === 0 && mode !== 'edit') errs.shapeTags = 'Please select how your project feels';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validate = () => validateStep('all');

  // ── Submit ────────────────────────────────────────────────────────────
  // Small files (<= LARGE_FILE_THRESHOLD) ride the normal multipart request
  // through Flask. Large files are held out, then uploaded directly to Azure
  // Blob Storage via SAS URL after the project exists, and registered with
  // the finalize endpoint. This bypasses Nginx/Gunicorn body size limits.
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) {
      // Show a toast so the user knows why save didn't work,
      // even if the error label is scrolled out of view
      const msgs = [];
      if (!name.trim()) msgs.push('Project name is required');
      else if (name.length > 100) msgs.push('Name must be under 100 characters');
      if (projectType === 'physical' && !location.trim()) msgs.push('Location required for physical projects');
      if (shapeTags.length === 0 && mode !== 'edit') msgs.push('Please select how your project feels');
      toast.error(msgs[0] || 'Please fix the errors above');
      return;
    }

    // ── Split files by size ──────────────────────────────────────────
    const smallFiles = files.filter(f => f.size <= LARGE_FILE_THRESHOLD);
    const largeFiles = files.filter(f => f.size > LARGE_FILE_THRESHOLD);

    // Guard: if the API client doesn't have the direct-upload functions
    // (api/index.js not updated yet), fail loudly instead of silently
    // sending huge files through Flask and hitting a 413.
    if (largeFiles.length > 0 &&
        (typeof projectsAPI.uploadFileDirect !== 'function' ||
         typeof projectsAPI.finalizeUpload !== 'function')) {
      toast.error('Large file uploads are not available yet — the API client is missing the direct upload functions.');
      return;
    }

    setIsSubmitting(true);

    // ── Step 1: Create/update the project (small files included) ─────
    let response;
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('description', description.trim());
      formData.append('project_type', projectType);
      formData.append('tags', JSON.stringify(tags));
      formData.append('shape_tags', shapeTags.join(','));
      if (projectType === 'physical' && location) formData.append('location', location);
      if (selectedParents.length > 0) {
        formData.append('parent_project_ids', JSON.stringify(selectedParents.map(p => p.id)));
        if (selectedParents.length === 1) formData.append('parent_project_id', selectedParents[0].id);
      } else if (mode === 'edit') {
        formData.append('parent_project_ids', JSON.stringify([]));
        formData.append('parent_project_id', '');
      }
      if (challenge) {
        formData.append('challenge_id', challenge.id);
        const cpid = challenge.project_id || challenge.projectId;
        if (cpid) formData.append('parent_project_id', cpid);
      }
      if (coverImage) formData.append('cover_image', coverImage);
      else if (mode === 'edit' && !coverPreview && (editProject?.image_url || editProject?.imageUrl)) {
        // User cleared the existing cover without uploading a replacement
        formData.append('remove_cover', 'true');
      }
      smallFiles.forEach(f => formData.append('files', f));
      if (filesToRemove.length > 0) formData.append('files_to_remove', JSON.stringify(filesToRemove));

      response = mode === 'edit' && editProject
        ? await projectsAPI.update(editProject.id, formData)
        : await projectsAPI.create(formData);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to save project' });
      setIsSubmitting(false);
      return;
    }

    // ── Step 2: Direct-to-blob uploads for large files ────────────────
    if (largeFiles.length > 0) {
      const projectId = mode === 'edit' && editProject
        ? editProject.id
        : (response.data?.project?.id || response.data?.id);

      if (!projectId) {
        // Project saved, but we can't attach the large files without an id.
        setUploadProgress(null);
        setIsSubmitting(false);
        toast.error('Project saved, but large files could not be attached (missing project id). Edit the project to add them.');
        onSuccess?.(response.data);
        return;
      }

      const failedFiles = [];
      for (let i = 0; i < largeFiles.length; i++) {
        const file = largeFiles[i];
        try {
          setUploadProgress({
            fileName: file.name,
            percent: 0,
            index: i + 1,
            total: largeFiles.length,
          });

          // Get a SAS upload URL from the backend
          const urlRes = await projectsAPI.generateUploadUrl(file.name);
          const { upload_url, blob_url, original_filename } = urlRes.data;

          // PUT directly to Azure Blob Storage via SAS URL
          await projectsAPI.uploadFileDirect(
            upload_url,
            file,
            (percent) => {
              setUploadProgress({
                fileName: file.name,
                percent,
                index: i + 1,
                total: largeFiles.length,
              });
            }
          );

          // Register the uploaded blob in the database
          await projectsAPI.finalizeUpload(projectId, blob_url, original_filename, file.size);

        } catch (err) {
          console.error(`Direct upload failed for ${file.name}:`, err);
          failedFiles.push(file.name);
        }
      }

      setUploadProgress(null);

      if (failedFiles.length > 0) {
        toast.error(
          `Project saved, but ${failedFiles.length} large file${failedFiles.length > 1 ? 's' : ''} failed to upload: ` +
          `${failedFiles.join(', ')}. Edit the project to try again.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`${largeFiles.length} large file${largeFiles.length > 1 ? 's' : ''} uploaded successfully`);
      }
    }

    setIsSubmitting(false);
    onSuccess?.(response.data);
  };

  const handleMinimize = () => { setIsMinimized(true); onMinimize?.(); };
  const handleMaximize = () => { setIsMinimized(false); onMaximize?.(); };

  // ── Title / icon helpers ──────────────────────────────────────────────
  const getTitle = () => {
    if (mode === 'edit') return 'Edit Project';
    if (mode === 'fork' || mode === 'multifork') return 'Fork Project';
    if (mode === 'challenge-response') return 'Respond to Challenge';
    return 'Create Project';
  };

  const getIcon = () => {
    if (mode === 'edit') return <Edit3 className="w-5 h-5 text-blue-400" />;
    if (mode === 'fork' || mode === 'multifork') return <GitFork className="w-5 h-5 text-purple-400" />;
    if (mode === 'challenge-response') return <Target className="w-5 h-5 text-amber-400" />;
    return <Plus className="w-5 h-5 text-purple-400" />;
  };

  const getSubmitText = () => {
    if (mode === 'edit') return 'Save Changes';
    if (mode === 'fork' || mode === 'multifork') return 'Create Fork';
    if (mode === 'challenge-response') return 'Submit Response';
    return 'Create Project';
  };

  // ── Mobile wizard navigation ──────────────────────────────────────────
  const handleNext = () => {
    // Validate current step before proceeding
    if (wizardStep === 0 && !validateStep(0)) return;
    if (wizardStep === 1 && !validateStep(1)) return;
    if (wizardStep < activeSteps.length - 1) {
      setWizardStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (wizardStep > 0) setWizardStep(prev => prev - 1);
  };

  const isLastStep = wizardStep === activeSteps.length - 1;

  // ═════════════════════════════════════════════════════════════════════
  // MINIMIZED DOCK — floating pill at bottom (shared mobile + desktop)
  // ═════════════════════════════════════════════════════════════════════
  if (isMinimized) {
    return (
      <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div
          className="flex items-center gap-3 px-5 py-3 bg-zinc-900/95 backdrop-blur-xl border border-purple-500/40 rounded-2xl shadow-2xl shadow-purple-500/10 cursor-pointer hover:border-purple-500/60 transition-all duration-200 hover:shadow-purple-500/20 group"
          onClick={handleMaximize}
        >
          <div className="relative flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-purple-500 animate-ping opacity-50" />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {getIcon()}
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate max-w-[200px]">
                {name || getTitle()}
              </p>
              <p className="text-[11px] text-zinc-400">
                {selectedParents.length > 0
                  ? `${selectedParents.length} parent${selectedParents.length !== 1 ? 's' : ''} · tap nodes to add more`
                  : 'Tap map nodes to add parents'}
              </p>
            </div>
          </div>
          {selectedParents.length > 0 && (
            <div className="flex items-center -space-x-2 ml-1">
              {selectedParents.slice(0, 3).map(parent => (
                <div key={parent.id} className={cn(
                  "w-7 h-7 rounded-full border-2 overflow-hidden flex-shrink-0 transition-all duration-500",
                  justAddedParent === parent.id ? "border-emerald-400 scale-110 ring-2 ring-emerald-400/30" : "border-zinc-700 bg-zinc-800"
                )}>
                  {(parent.image_url || parent.imageUrl)
                    ? <img src={parent.image_url || parent.imageUrl} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-zinc-500 text-[10px] font-bold">{parent.name?.[0]}</div>
                  }
                </div>
              ))}
              {selectedParents.length > 3 && (
                <div className="w-7 h-7 rounded-full border-2 border-zinc-700 bg-zinc-800 flex items-center justify-center text-[10px] text-zinc-400 font-medium flex-shrink-0">
                  +{selectedParents.length - 3}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5 ml-2 pl-3 border-l border-zinc-700">
            <Maximize2 className="w-4 h-4 text-zinc-400 group-hover:text-purple-400 transition-colors" />
          </div>
          <button onClick={(e) => { e.stopPropagation(); onClose?.(); }} className="p-1 hover:bg-zinc-700 rounded-full transition-colors">
            <X className="w-3.5 h-3.5 text-zinc-500 hover:text-zinc-300" />
          </button>
        </div>
        {justAddedParent && (
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full animate-in fade-in slide-in-from-bottom-2 duration-200">
            <span className="text-xs text-emerald-400 font-medium whitespace-nowrap">Parent added — tap to expand</span>
          </div>
        )}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // MOBILE: Full-screen bottom sheet wizard
  // ═════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        {flagTarget && (
          <AttributionFlagModal project={flagTarget} onClose={() => setFlagTarget(null)} onSubmit={handleFlagSubmit} />
        )}
        <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col safe-area-top safe-area-bottom animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50">
            <div className="flex items-center gap-2.5">
              {wizardStep > 0 ? (
                <button onClick={handleBack} className="p-2 -ml-2 rounded-xl active:bg-zinc-800">
                  <ChevronLeft className="w-5 h-5 text-zinc-400" />
                </button>
              ) : (
                <button onClick={onClose} className="p-2 -ml-2 rounded-xl active:bg-zinc-800">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              )}
              <div>
                <h2 className="text-base font-semibold text-white">{getTitle()}</h2>
                <p className="text-xs text-zinc-500">{activeSteps[wizardStep].label}</p>
              </div>
            </div>
            {/* Skip optional steps */}
            {(wizardStep === 2 || wizardStep === 3) && (
              <button onClick={handleNext} className="text-sm text-purple-400 active:text-purple-300 py-1 px-3 rounded-lg active:bg-purple-500/10">
                Skip
              </button>
            )}
          </div>

          {/* Progress bar */}
          <WizardProgressBar currentStep={wizardStep} totalSteps={activeSteps.length} steps={activeSteps} />

          {/* Step content */}
          <div className="flex-1 overflow-hidden">
            {wizardStep === 0 && (
              <StepBasics
                name={name} setName={setName}
                description={description} setDescription={setDescription}
                coverPreview={coverPreview} onCoverChange={handleCoverChange} onCoverClear={handleCoverClear}
                coverInputRef={coverInputRef} errors={errors}
              />
            )}
            {wizardStep === 1 && (
              <StepTypeAndFeel
                projectType={projectType} setProjectType={setProjectType}
                location={location} setLocation={setLocation}
                shapeTags={shapeTags} toggleShapeTag={toggleShapeTag}
                tags={tags} setTags={setTags} tagInput={tagInput} setTagInput={setTagInput}
                addTag={addTag} removeTag={removeTag} handleTagKeyDown={handleTagKeyDown}
                errors={errors}
              />
            )}
            {wizardStep === 2 && (
              <StepConnections
                mode={mode} challenge={challenge}
                selectedParents={selectedParents} removeParent={removeParent}
                parentSearchQuery={parentSearchQuery} setParentSearchQuery={setParentSearchQuery}
                parentSearchResults={parentSearchResults}
                showParentSearch={showParentSearch} setShowParentSearch={setShowParentSearch}
                addParent={addParent} onMinimize={handleMinimize}
                editProject={editProject} setFlagTarget={setFlagTarget}
                justAddedParent={justAddedParent}
              />
            )}
            {wizardStep === 3 && (
              <StepFiles
                files={files} setFiles={setFiles}
                existingFiles={existingFiles} removeExistingFile={removeExistingFile}
                fileInputRef={fileInputRef}
              />
            )}
            {wizardStep === 4 && (
              <StepReview
                name={name} description={description} projectType={projectType}
                tags={tags} shapeTags={shapeTags} location={location}
                coverPreview={coverPreview} selectedParents={selectedParents}
                challenge={challenge} files={files} existingFiles={existingFiles}
                mode={mode} errors={errors} isSubmitting={isSubmitting}
                getSubmitText={getSubmitText}
              />
            )}
          </div>

          {/* Bottom nav */}
          <div className="px-4 py-3 border-t border-zinc-800/50 bg-zinc-950">
            {uploadProgress && (
              <div className="mb-3">
                <UploadProgressPanel progress={uploadProgress} />
              </div>
            )}
            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={cn(
                  'w-full py-4 rounded-2xl text-base font-semibold transition-all active:scale-[0.98]',
                  'bg-gradient-to-r from-purple-600 to-indigo-600 text-white',
                  'shadow-lg shadow-purple-500/25',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {uploadProgress
                      ? `Uploading ${uploadProgress.percent}%`
                      : mode === 'edit' ? 'Saving...' : 'Creating...'}
                  </span>
                ) : getSubmitText()}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="w-full py-4 rounded-2xl text-base font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25 active:scale-[0.98] transition-all"
              >
                <span className="flex items-center justify-center gap-2">
                  Next
                  <ChevronRight className="w-5 h-5" />
                </span>
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // DESKTOP: Polished centered scroll form (existing layout preserved)
  // ═════════════════════════════════════════════════════════════════════
  return (
    <>
      {flagTarget && (
        <AttributionFlagModal project={flagTarget} onClose={() => setFlagTarget(null)} onSubmit={handleFlagSubmit} />
      )}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overlay-backdrop">
        <div
          ref={overlayRef}
          className="relative w-full max-w-2xl max-h-[90vh] bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col animate-overlay-in"
          style={{ boxShadow: '0 25px 60px -12px rgba(0,0,0,0.5), 0 0 40px -10px rgba(139,92,246,0.15)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              {getIcon()}
              <h2 className="text-xl font-semibold text-white">{getTitle()}</h2>
            </div>
            <div className="flex items-center gap-1">
              {mode !== 'edit' && (
                <button onClick={handleMinimize} className="p-2 hover:bg-zinc-800 rounded-full transition-colors group" title="Minimize — browse map to add parents">
                  <Minimize2 className="w-4.5 h-4.5 text-zinc-400 group-hover:text-purple-400 transition-colors" />
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full transition-colors">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">

              {/* Parent projects section */}
              {(mode === 'edit' || mode === 'fork' || mode === 'multifork' || selectedParents.length > 0) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-zinc-300">
                      {mode === 'edit'
                        ? 'Lineage — Inspired By / Forked From'
                        : selectedParents.length === 1 ? 'Forking From' : 'Parent Projects'}
                    </label>
                    {mode !== 'edit' ? (
                      <button type="button" onClick={handleMinimize} className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors">
                        <Minimize2 className="w-3 h-3" />
                        Browse map to add more
                      </button>
                    ) : (
                      <span className="text-[11px] text-zinc-500">Add or remove sources. Changes affect revenue sharing.</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedParents.map(parent => (
                      <div key={parent.id} className={cn(
                        "flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg transition-all duration-300",
                        justAddedParent === parent.id ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-zinc-800 border border-transparent"
                      )}>
                        <div className="w-8 h-8 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
                          {(parent.image_url || parent.imageUrl)
                            ? <img src={parent.image_url || parent.imageUrl} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs font-bold">{parent.name?.[0]}</div>
                          }
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm text-zinc-200 max-w-[120px] truncate leading-tight">{parent.name}</span>
                          {parent.author?.username && (
                            <span className="text-[11px] text-zinc-500">@{parent.author.username}</span>
                          )}
                        </div>
                        <button type="button" onClick={() => removeParent(parent.id)} className="p-0.5 hover:bg-zinc-700 rounded ml-0.5">
                          <X className="w-3 h-3 text-zinc-500 hover:text-red-400 transition-colors" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {selectedParents.length === 0 && mode === 'edit' && (
                    <p className="text-xs text-zinc-600 mb-3 leading-relaxed">
                      No parent projects linked. If this work was inspired by another project on Enume,
                      linking it here credits the original creator and connects your lineage on the map.
                    </p>
                  )}

                  {mode === 'edit' && (
                    <div className="flex items-start gap-2 mb-3 p-3 bg-zinc-800/40 border border-zinc-700/50 rounded-lg">
                      <Flag className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Know of a project you should be crediting that isn't listed?
                        Add it above, or{' '}
                        <button type="button" onClick={() => setFlagTarget(editProject)}
                          className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors">
                          flag this project
                        </button>
                        {' '}for a moderator to review.
                      </p>
                    </div>
                  )}

                  <div className="relative">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="text" value={parentSearchQuery} onChange={(e) => setParentSearchQuery(e.target.value)}
                        onFocus={() => setShowParentSearch(true)}
                        placeholder={mode === 'edit' ? 'Search to add a source project...' : 'Search to add more parent projects...'}
                        className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-purple-500/50 transition-colors" />
                    </div>
                    {showParentSearch && parentSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                        {parentSearchResults.map(project => (
                          <button key={project.id} type="button" onClick={() => addParent(project)}
                            className="w-full flex items-center gap-3 p-2 hover:bg-zinc-700 transition-colors text-left">
                            <div className="w-8 h-8 rounded bg-zinc-600 overflow-hidden flex-shrink-0">
                              {(project.image_url || project.imageUrl)
                                ? <img src={project.image_url || project.imageUrl} alt="" className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-zinc-400">{project.name?.[0]}</div>
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{project.name}</p>
                              <p className="text-xs text-zinc-500">by @{project.author?.username}</p>
                            </div>
                            <Plus className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Challenge context */}
              {challenge && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-300">Responding to Challenge</p>
                      <p className="text-sm text-zinc-400 mt-1">{challenge.challenge_title || challenge.title}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cover image */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Cover Image <span className="text-zinc-500">(optional)</span></label>
                <div onClick={() => coverInputRef.current?.click()}
                  className={cn('relative h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors',
                    coverPreview ? 'border-transparent' : 'border-zinc-700 hover:border-zinc-600')}>
                  {coverPreview ? (
                    <>
                      <img src={coverPreview} alt="Cover" className="w-full h-full object-cover rounded-xl" />
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleCoverClear(); }}
                        className="absolute top-2 right-2 p-1.5 bg-zinc-900/80 hover:bg-zinc-800 rounded-full">
                        <X className="w-4 h-4 text-zinc-400" />
                      </button>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
                      <Image className="w-8 h-8 mb-2" />
                      <p className="text-sm">Click to upload cover image</p>
                    </div>
                  )}
                </div>
                <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverChange} className="hidden" />
              </div>

              <Input label="Project Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Give your project a name" error={errors.name} required />
              {/* Typing [[ links another project (backlinks) */}
              <MentionTextarea label="Description" value={description} onChange={setDescription} placeholder="Describe your project..." rows={4} />

              {/* Project type */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Project Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(PROJECT_TYPES).map(([key, type]) => {
                    const icons = { physical: Package, digital: Monitor, hybrid: Layers };
                    const Icon = icons[key];
                    return (
                      <button key={key} type="button" onClick={() => setProjectType(key)}
                        className={cn('flex items-center gap-2 p-3 rounded-xl border transition-all',
                          projectType === key ? 'border-purple-500/50 bg-purple-500/10 text-white' : 'border-zinc-700/50 bg-zinc-800/30 text-zinc-400 hover:border-zinc-600')}>
                        {Icon && <Icon className="w-4 h-4" />}
                        <span className="text-sm">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {projectType === 'physical' && (
                <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Where is this physical project?" icon={MapPin} error={errors.location} />
              )}

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {tags.map(tag => {
                    const physGroup = Object.values(PHYSICAL_TAGS).find(g => g.tags.includes(tag));
                    const pillColor = physGroup
                      ? physGroup.color === 'emerald' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                      : physGroup.color === 'amber' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                      : 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                      : 'bg-zinc-800 text-zinc-300 border-zinc-700/50';
                    return (
                      <span key={tag} className={cn('flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm border', pillColor)}>
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="ml-0.5 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </span>
                    );
                  })}
                </div>
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={handleTagKeyDown}
                  enterKeyHint="done"
                  placeholder="Type a tag..."
                  className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-4 py-2 text-sm text-white placeholder:text-zinc-500" />
                <p className="text-[11px] text-zinc-600 mt-1">Press Enter after each tag — tags determine your project's zone on the map</p>
                <div className="mt-3 space-y-2">
                  {Object.entries(ZONE_TAGS).map(([zoneId, zoneTags]) => {
                    const available = zoneTags.filter(t => !tags.includes(t));
                    if (available.length === 0) return null;
                    const zone = ZONES[zoneId];
                    if (!zone) return null;
                    return (
                      <div key={zoneId} className="flex items-start gap-2">
                        <div className="flex items-center gap-1 pt-0.5 flex-shrink-0 min-w-[70px]">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: zone.color }} />
                          <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: zone.color }}>{zone.shortLabel}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {available.slice(0, 3).map(s => (
                            <button key={s} type="button" onClick={() => addTag(s)}
                              className="px-2 py-0.5 text-xs border rounded-full transition-colors hover:bg-zinc-800"
                              style={{ color: `${zone.color}cc`, borderColor: `${zone.color}30` }}>
                              + {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Physical / Hybrid — marketplace & condition tag suggestions */}
                {(projectType === 'physical' || projectType === 'hybrid') && (
                  <div className="mt-3 space-y-2.5">
                    {Object.entries(PHYSICAL_TAGS).map(([groupKey, group]) => {
                      const unselected = group.tags.filter(t => !tags.includes(t));
                      if (unselected.length === 0) return null;
                      const colorMap = { emerald: 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10', amber: 'text-amber-400 border-amber-500/30 hover:bg-amber-500/10', violet: 'text-violet-400 border-violet-500/30 hover:bg-violet-500/10' };
                      return (
                        <div key={groupKey}>
                          <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1">{group.label}</p>
                          <div className="flex flex-wrap gap-1">
                            {unselected.map(t => (
                              <button key={t} type="button" onClick={() => addTag(t)}
                                className={cn('px-2 py-0.5 text-xs border rounded-full transition-colors', colorMap[group.color] || 'text-zinc-500 border-zinc-800')}>
                                + {t}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Shape / Feel Picker */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-zinc-300">
                    How does it feel? <span className="text-red-400">*</span>
                  </label>
                  <span className={cn(
                    'text-[11px] font-medium',
                    shapeTags.length === 0 ? 'text-zinc-600' :
                    shapeTags.length === 2 ? 'text-violet-400' : 'text-zinc-500'
                  )}>
                    {shapeTags.length}/2 selected
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {SHAPE_OPTIONS.map(shape => {
                    const isSelected = shapeTags.includes(shape.id);
                    const isDisabled = !isSelected && shapeTags.length >= 2;
                    return (
                      <button key={shape.id} type="button" onClick={() => toggleShapeTag(shape.id)}
                        disabled={isDisabled} title={shape.label}
                        className={cn(
                          'flex flex-col items-center justify-center gap-1 py-3 rounded-xl border transition-all',
                          isSelected
                            ? 'bg-violet-500/20 border-violet-500/60 text-violet-300 scale-105 shadow-lg shadow-violet-500/20'
                            : isDisabled
                              ? 'bg-zinc-800/20 border-zinc-800 text-zinc-700 cursor-not-allowed'
                              : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-violet-500/40 hover:text-zinc-200 hover:bg-zinc-800'
                        )}>
                        <span className="text-xl leading-none">{shape.symbol}</span>
                        <span className="text-[10px] font-medium leading-none">{shape.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.shapeTags && <p className="text-xs text-red-400 mt-1.5">{errors.shapeTags}</p>}
                <p className="text-[11px] text-zinc-600 mt-1.5">Pick 1 or 2 shapes that capture the feeling of your work</p>
              </div>

              {/* Files */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Project Files <span className="text-zinc-500">(optional)</span></label>
                {existingFiles.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {existingFiles.map(f => (
                      <div key={f.id} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
                        <FileText className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-300 flex-1 truncate">{f.name}</span>
                        <button type="button" onClick={() => removeExistingFile(f.id)} className="p-1 hover:bg-zinc-700 rounded">
                          <Trash2 className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {files.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
                        <FileText className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-300 flex-1 truncate">{f.name}</span>
                        {f.size > LARGE_FILE_THRESHOLD && (
                          <span className="text-[10px] font-medium text-purple-400 bg-purple-500/10 border border-purple-500/30 px-1.5 py-0.5 rounded-full">
                            direct upload
                          </span>
                        )}
                        <span className="text-xs text-zinc-600">{formatFileSize(f.size)}</span>
                        <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="p-1 hover:bg-zinc-700 rounded">
                          <Trash2 className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn('border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all',
                    dragActive ? 'border-purple-500 bg-purple-500/5' : 'border-zinc-700 hover:border-zinc-600')}>
                  <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">Drop files here or <span className="text-purple-400">browse</span></p>
                  <p className="text-xs text-zinc-600 mt-1">85+ file types · large files upload straight to cloud storage</p>
                </div>
                <input ref={fileInputRef} type="file" multiple onChange={handleFilesChange} className="hidden" />
              </div>

              {errors.submit && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{errors.submit}</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 p-6 border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-xl">
              {uploadProgress && (
                <div className="mb-3">
                  <UploadProgressPanel progress={uploadProgress} />
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button type="submit" loading={isSubmitting}>
                  {uploadProgress ? `Uploading ${uploadProgress.percent}%` : getSubmitText()}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateProjectOverlay;